import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, TrendingUp, Shield, BarChart3, DollarSign } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import rbhm from './images1/RBHpechnology.png';
const roiData = [
  { item: 'Daily CBG Output', value: '10,000 kg/day' },
  { item: 'Daily Revenue', value: '₹5.5 lakh' },
  { item: 'Monthly Revenue (30 days)', value: '₹1.65 crore' },
  { item: 'Annual Revenue', value: '₹20+ crore' },
  { item: 'Annual Monitoring Cost (RBHM)', value: '₹6 lakh (~0.3% of revenue)' },
];
// ✅ Framer Motion variants (MUST be above component)
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const item = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0 }
};
const failureScenarios = [
  {
    scenario: 'A. Upset, slow recovery',
    production: 'Drops to 20% (30 days), then 80% (30 days)',
    duration: '60 days',
    revenueLost: '₹1.65 crore',
    profitLost: '₹33 lakh'
  },
  {
    scenario: 'B. Prolonged 50% output',
    production: '50% reduction',
    duration: '60 days',
    revenueLost: '₹1.65 crore',
    profitLost: '₹33 lakh'
  },
  {
    scenario: 'C. Severe shutdown',
    production: '100% loss',
    duration: '90 days',
    revenueLost: '₹4.95 crore',
    profitLost: '₹99 lakh'
  }
];

const comparisonData = [
  {
    item: 'Typical equipment cost (CAPEX)',
    gcHplc: 'GC-FID: ₹25-60 Lakh\nHPLC: ₹20-40 Lakh',
    rbhm: 'Field kit: ₹1-2 Lakh per site'
  },
  {
    item: 'Qualification of technician',
    gcHplc: 'M.Sc./B.Sc. (Chemistry/Biotech) with analytical lab experience',
    rbhm: 'Basic education (10th pass) + short practical training'
  },
  {
    item: 'Technician training (time)',
    gcHplc: '3-6 months (method validation)',
    rbhm: '2-7 days on-site practical training'
  },
  {
    item: 'Monthly technician pay',
    gcHplc: '₹40,000 – ₹80,000',
    rbhm: '₹8,000 – ₹25,000'
  },
  {
    item: 'Lab infrastructure cost',
    gcHplc: 'High (₹10-15 Lakh or more)',
    rbhm: 'Minimal (₹1-₹2 Lakh)'
  },
  {
    item: 'Consumables per test',
    gcHplc: 'High — ₹300 – ₹500+ / sample',
    rbhm: 'Very low — ₹20 – ₹50 / test'
  },
  {
    item: 'Time to actionable plan',
    gcHplc: 'Hours to days (24-72 hrs or more)',
    rbhm: 'Within hours (same day)'
  },
  {
    item: 'Annual maintenance / AMC',
    gcHplc: '8-15% of CAPEX (₹2–9 Lakh/yr)',
    rbhm: 'Low: ₹10k-80k/yr/site'
  }
];

export const RBHMPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="rbhm-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Proprietary Technology
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              RBHM™ Technology
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto leading-relaxed tracking-wider">
              RENVITEK's proprietary biodigester health intelligence and decision-support framework 
              for stable, efficient, and long-term operation of anaerobic digesters.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Message */}
      <section className="py-16 bg-amber-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            {[
              { icon: AlertTriangle, title: 'Early Warning', desc: 'Detects instability weeks in advance' },
              { icon: DollarSign, title: 'Low Cost', desc: 'Only ₹6 lakh/year' },
              { icon: Shield, title: 'Prevents Shutdowns', desc: 'Saves ₹1.6–5 crore per failure' },
              { icon: TrendingUp, title: 'Protects ROI', desc: 'Ensures stable payback' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                className="bg-white/20 backdrop-blur p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <item.icon className="w-10 h-10 text-emerald-950 mx-auto mb-3" />
                <h3 className="font-bold text-emerald-950">{item.title}</h3>
                <p className="text-emerald-900 text-sm tracking-wider">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                What is RBHM™ Technology?
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
                <p>
                 RBHM Technology is RENVITEK’s proprietary biodigester health intelligence and decision support framework, developed by RENVITEK ENGINEERS LLP to ensure stable, efficient, and long-term operation of anaerobic digesters in Biogas and CBG plants. 
                </p>
                <p>
                  RBHM emphasizes continuous assessment of both biological and process health of the digester, rather than relying solely on gas production metrics.
                </p>
                <p>
                  The system integrates early-warning indicators, advanced process diagnostics, and corrective control strategies to proactively prevent process instability, scum formation, digester souring, and associated yield losses.
                </p>
                <p>
                  <div className="space-y-6 tracking-wider leading-relaxed text-slate-600 dark:text-white/70 text-base">
                {[
                  {
                    title: 'Why RBHM is different:',
                    items: ['Goes beyond SCADA-only monitoring', 'Focuses on biology-centric control, not just mechanical data', 'Developed from pilot-scale, commercial plant data, and peer-reviewed research', 'Especially effective for food waste, market waste, and mixed organic substrates where instability and scum are common']
                  },
                  ].map((section) => (
                  <div key={section.title}>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-2">{section.title}</h4>
                    <ul className="space-y-1">
                      {section.items.map((item) => (
                        <li key={item} className="text-sm text-slate-600 dark:text-white/70 flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={rbhm}
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
            
          </div>
        </div>
      </section>
      {/* key components */}
     <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
  <div className="w-full px-6 lg:px-16">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
            >
              <h2 className="text-xl font-bold text-emerald-900 dark:text-white mb-6 text-center">Key Technical Components</h2>
              <div className="space-y-6 tracking-wider leading-relaxed text-slate-600 dark:text-white/70 grid lg:grid-cols-2 gap-16">
                {[
                  {
                    title: 'Biological Health Indicators',
                    items: ['VFA/Alkalinity ratio', 'pH buffering capacity', 'Volatile fatty acid profiles', 'Ammonia and inhibitory thresholds', 'TS/VS destruction efficiency']
                  },
                  {
                    title: 'Process Performance Analytics',
                    items: ['Organic Loading Rate (OLR) optimization', 'Hydraulic Retention Time (HRT) validation', 'Gas yield per kg VS reduced', 'Methane concentration trends', 'Mixing and stratification behaviour (scum/foam risk)']
                  },
                  {
                    title: 'Early Warning & Predictive Control',
                    items: ['Identification of pre-failure conditions before gas drop occurs', 'Feedstock variability analysis', 'Load adjustment protocols', 'Recovery guidelines']
                  },
                  {
                    title: 'Operational Decision Support',
                    items: ['Plant-specific operating envelopes', 'SOPs for stable continuous operation', 'Troubleshooting matrices for biological upsets', 'Long-term performance benchmarking']
                  }
                ].map((section) => (
                  <div key={section.title}>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-2">{section.title}</h4>
                    <ul className="space-y-1">
                      {section.items.map((item) => (
                        <li key={item} className="text-sm text-slate-600 dark:text-white/70 flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
            
            </div>
            </section>
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
          <h1 className="text-xl font-semibold text-amber-400 mb-3">
            Why Biogas Plant Health Monitoring Is Essential for Sustained Revenue in the CBG Industry
          </h1>

          <div className="mb-8">
            <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              A Digester Failure Is a Revenue Failure
            </h3>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>In a biogas–CBG plant, the anaerobic digester is the primary revenue-generating asset.</li>
              <li>Any decline in biological performance immediately reduces gas production—disrupting upgrading operations, affecting supply commitments, and directly impacting cash flow.</li>
              <li>Unlike mechanical issues, biological failures cannot be resolved overnight; recovery often takes months, resulting in substantial financial losses.</li>
              <li>Digester health monitoring is not optional—it is a financial risk-management strategy.</li>
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              Why Digester Health Monitoring Matters
            </h3>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              Effective monitoring directly supports business stability by:
            </p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>Protecting cash flow through prevention of avoidable production losses</li>
              <li>Preserving project economics by minimizing prolonged downtime</li>
              <li>Building confidence among investors, lenders, and Oil Marketing Companies (OMCs) through consistent and reliable performance</li>
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              The Hidden Cost of Biological Instability
            </h3>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              When digester imbalance goes undetected, operating costs rise sharply due to:
            </p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>Increased chemical consumption (alkalinity correction, nutrients, buffering agents)</li>
              <li>Higher manpower and laboratory testing requirements</li>
              <li>Additional energy use for intensified mixing and pumping</li>
              <li>Penalties and revenue losses from non-compliance with LOI or PPA commitments</li>
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              From Reactive Fixes to Predictable Performance
            </h3>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              Continuous monitoring of key biological indicators—pH, VFAs, alkalinity, and stability ratios—enables:
            </p>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>Early detection of imbalance before gas production is affected</li>
              <li>Timely corrective actions within days instead of months</li>
              <li>Stable methane yields and predictable, bankable revenue</li>
            </ul>
          </div>

          <div>
            <h3 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
              The Business Reality
            </h3>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>Long recovery periods translate into significant financial losses</li>
              <li>Protecting continuous gas output means protecting revenue</li>
              <li>Continuous digester health monitoring acts as insurance for investors</li>
            </ul>
          </div>
        </motion.div>
        </div>
      </section>
      {/* ROI Impact */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    {/* SECTION HEADER */}
    <motion.div
      className="text-center mb-12"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-4">
        ROI Impact Analysis
      </h2>
      <p className="text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
        10 TPD CBG Plant (200 TPD Napier Grass feedstock, CBG price ₹55/kg)
      </p>
    </motion.div>

    {/* GRID FOR FIRST TWO TABLES */}
    <div className="grid lg:grid-cols-2 gap-8">

      {/* TABLE 1 – PLANT ECONOMICS */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-4 flex items-center gap-2">
          <BarChart3 className="text-emerald-500" />
          Plant Economics
        </h3>

        <div className="bg-white dark:bg-emerald-900 rounded-lg overflow-hidden shadow-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-emerald-900">
                <TableHead className="text-white">Item</TableHead>
                <TableHead className="text-white">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roiData.map((row) => (
                <TableRow key={row.item}>
                  <TableCell className="font-medium">{row.item}</TableCell>
                  <TableCell className="text-emerald-600 dark:text-emerald-400 font-semibold">
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </motion.div>

      {/* TABLE 2 – FAILURE SCENARIOS */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="text-red-500" />
          What Happens if the Digester Fails?
        </h3>

        <div className="bg-white dark:bg-emerald-900 rounded-lg overflow-hidden shadow-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-red-700">
                <TableHead className="text-white">Scenario</TableHead>
                <TableHead className="text-white">Production Loss</TableHead>
                <TableHead className="text-white">Duration</TableHead>
                <TableHead className="text-white">Revenue Lost</TableHead>
                <TableHead className="text-white">Loss</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {failureScenarios.map((row) => (
                <TableRow key={row.scenario}>
                  <TableCell className="font-medium text-sm">{row.scenario}</TableCell>
                  <TableCell>{row.production}</TableCell>
                  <TableCell>{row.duration}</TableCell>
                  <TableCell>{row.revenueLost}</TableCell>
                  <TableCell className="text-red-600 font-semibold">
                    {row.profitLost}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <p className="text-sm text-slate-500 dark:text-white/50 mt-2 tracking-wider">
          *Assuming 20% profit margin
        </p>
      </motion.div>
    </div>

    {/* FULL-WIDTH FINAL TABLE */}
    <motion.div
      className="mt-16"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-6 flex items-center justify-center gap-2 text-center">
        <TrendingUp className="text-emerald-600" />
        Financial Impact of Biogas Plant Health Monitoring (10 TPD CBG Plant)
      </h3>

      <div className="overflow-x-auto rounded-xl border border-emerald-200 dark:border-emerald-800">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-emerald-700 text-white">
              <th className="px-6 py-4 font-semibold">Particulars</th>
              <th className="px-6 py-4 font-semibold">
                Without Monitoring<br />
                <span className="text-amber-300 text-sm">(Failure Scenario)</span>
              </th>
              <th className="px-6 py-4 font-semibold">
                With <span className="text-amber-300">RENVITEK RBHM</span>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-emerald-200 dark:divide-emerald-800">
           <tr>
        <td className="px-6 py-4 font-medium">Daily CBG Production</td>
        <td className="px-6 py-4 text-slate-700 dark:text-white/70">
          10,000 kg <span className="text-red-600 font-semibold">(₹5.5 lakh)</span>
        </td>
        <td className="px-6 py-4 text-emerald-700 dark:text-emerald-400 font-semibold">
          10,000 kg (₹5.5 lakh revenue)
        </td>
      </tr>

      <tr className="bg-emerald-50 dark:bg-emerald-900/40">
        <td className="px-6 py-4 font-medium">Annual Revenue Potential</td>
        <td className="px-6 py-4 font-semibold text-slate-800 dark:text-white">
          ₹20+ crore
        </td>
        <td className="px-6 py-4 font-semibold text-emerald-700 dark:text-emerald-400">
          ₹20+ crore
        </td>
      </tr>

      <tr>
        <td className="px-6 py-4 font-medium">
          Scenario A <span className="text-sm">(60 days upset)</span>
        </td>
        <td className="px-6 py-4">
          <span className="block text-red-600 font-semibold">
            Revenue loss: ₹1.65 crore
          </span>
          <span className="block text-red-500">
            Profit loss: ₹33 lakh
          </span>
        </td>
        <td className="px-6 py-4">
          <span className="block text-emerald-700 font-semibold">
            Loss prevented: ₹1.65 crore
          </span>
          <span className="block text-emerald-600">
            Profit protected: ₹33 lakh
          </span>
        </td>
      </tr>

      <tr className="bg-emerald-50 dark:bg-emerald-900/40">
        <td className="px-6 py-4 font-medium">
          Scenario B <span className="text-sm">(50% output for 60 days)</span>
        </td>
        <td className="px-6 py-4">
          <span className="block text-red-600 font-semibold">
            Revenue loss: ₹1.65 crore
          </span>
          <span className="block text-red-500">
            Profit loss: ₹33 lakh
          </span>
        </td>
        <td className="px-6 py-4">
          <span className="block text-emerald-700 font-semibold">
            Loss prevented: ₹1.65 crore
          </span>
          <span className="block text-emerald-600">
            Profit protected: ₹33 lakh
          </span>
        </td>
      </tr>

      <tr>
        <td className="px-6 py-4 font-medium">
          Scenario C <span className="text-sm">(90 days shutdown)</span>
        </td>
        <td className="px-6 py-4">
          <span className="block text-red-700 font-bold">
            Revenue loss: ₹4.95 crore
          </span>
          <span className="block text-red-600">
            Profit loss: ₹99 lakh
          </span>
        </td>
        <td className="px-6 py-4">
          <span className="block text-emerald-800 font-bold">
            Loss prevented: ₹4.95 crore
          </span>
          <span className="block text-emerald-700">
            Profit protected: ₹99 lakh
          </span>
        </td>
      </tr>

      <tr className="bg-amber-50 dark:bg-amber-900/30">
        <td className="px-6 py-4 font-medium">Monitoring Cost (RBHM)</td>
        <td className="px-6 py-4 text-slate-600 dark:text-white/70">
          Not applicable
        </td>
        <td className="px-6 py-4 font-semibold text-amber-700 dark:text-amber-400">
          ₹6 lakh / year <span className="text-sm">(≈0.3% of revenue)</span>
        </td>
      </tr>

      <tr className="bg-emerald-700 text-white">
        <td className="px-6 py-4 font-bold">Net Impact on ROI</td>
        <td className="px-6 py-4">
          Payback delayed by <span className="font-bold">2–6 months</span>
        </td>
        <td className="px-6 py-4 font-bold text-amber-300">
          Payback safeguarded, uninterrupted revenue flow
        </td>
      </tr>
          </tbody>
        </table>
      </div>
    </motion.div>

  </div>
</section>
      
      

      {/* key concept */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
      <div className="w-full px-6 lg:px-16 text-white tracking-wider leading-relaxed">

        {/* ================= KEY MESSAGE ================= */}
        <motion.h2
          className="text-3xl font-bold text-amber-400 mb-6 mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Key Message for Clients
        </motion.h2>

        <motion.ul
          className="space-y-3 list-disc pl-6 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider mb-14"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {[
            'Early warning system: Detects instability weeks in advance.',
            'Low-cost solution: Only 6 lakh/year.',
            'Prevents costly shutdowns → saves 1.6–5 crore losses per failure.',
            'ROI impact: Protects revenue, ensures stable payback, builds investor confidence.',
            'Bottom line: RBHM is like an “insurance policy” for your digester health.',
            'RBHM Monitoring = 6 lakh/year → Protects 1.6–5 crore in revenue + ensures contract reliability.'
          ].map((text, i) => (
            <motion.li key={i} variants={item}>
              {text}
            </motion.li>
          ))}
        </motion.ul>

        {/* ================= COMPANY OVERVIEW ================= */}
        <motion.h3
          className="text-2xl font-semibold text-emerald-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          RENVITEK Engineers LLP – Pioneering Long-Term Biogas Plant Sustainability
        </motion.h3>
        <motion.div
          className="space-y-6 text-white/80 leading-relaxed mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            At RENVITEK Engineers LLP, we specialize in a service that most technology providers
            overlook: ensuring the biochemical health and long-term resilience of anaerobic digesters.
            While many companies focus on building plants, we focus on keeping them alive, efficient,
            and profitable over decades of operation.
          </p>

          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            Our approach is built on decades of practical experience in running and troubleshooting
            commercial biogas plants combined with peer-reviewed scientific research on early warning
            indicators for digester stability
          </p>

          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            Anaerobic digestion (AD) is the “heart” of any biogas-CBG Industry. If the digester fails or
            underperforms, the entire business model collapses—no gas, no upgrading, no revenue.
            Unlike mechanical faults (which can be fixed by replacing equipment), biological failures
            are much more expensive and take months to recover.
          </p>

          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            RENITEK will provide on-site laboratory and consulting services a specialized Biogas plant
            health monitoring (RBHM) technology for the well-being of the anaerobic environment and
            the continuous production of biogas. It is very important to monitor the biological
            efficacy of the biomasses, in order to control the operating costs of the plant and to
            produce revenues in line with expected performances.
          </p>

          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            RENVITEK will act as a consultant before, during and after the construction of the plant,
            offering a customised Biogas plant health care service based on the particular
            characteristics of the plant.
          </p>
        </motion.div>

        {/* ================= PRINCIPLE ================= */}
        <motion.h3
          className="text-2xl font-semibold text-emerald-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Principle Behind RENVITEK Biodigester Health Monitoring (RBHM) Technology
        </motion.h3>

        <motion.div
          className="space-y-6 text-white/80 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            The RBHM technology is built on a foundation of decades of real-time biogas plant
            operational data and peer-reviewed research insights. The central principle is that each
            anaerobic digester exhibits its own set of early warning indicators, biochemical ratios
            and stability markers that signal potential process imbalance well before System failure
            occurs.
          </p>

          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            At RENVITEK, every new plant undergoes an initial diagnostic phase (3 months), during
            which these site-specific indicators are identified, validated, and standardized. Once
            confirmed, the digester is continuously monitored using a simplified titration-based
            method, a proprietary protocol uniquely developed by RENVITEK.
          </p>

          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            Unlike conventional practices that depend on expensive instruments and highly skilled
            operators, RBHM technology is designed for practical, everyday use. It does not require
            specialized technical manpower, even a single operator with basic education (a 10th
            class pass) can reliably perform the monitoring.
          </p>

          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            This makes the technology low-cost, scalable, and highly accessible, while still
            delivering precise process control. Most importantly, RBHM ensures uninterrupted
            feeding and stable operation, enabling biogas–CBG plants to run successfully for years
            without downtime due to digester instability.
          </p>
        </motion.div>

        {/* ================= OPERATIONAL MODEL ================= */}
        <motion.h3
          className="text-2xl font-semibold text-emerald-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          What is the operational Model of RENVITEK Biodigester Health Monitoring (RBHM)?
        </motion.h3>

        <motion.p
          className="text-white/80 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Remote Monitoring and Proactive Diagnostics
          <p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            RENVITEK offers cutting-edge, preventive, and cost-saving remote monitoring and Proactive 
diagnostic services for biogas–CBG plants. By combining our proprietary RBHM technology 
with digital data access, we continuously track the biochemical health of your digester and detect 
deviations in early warning indicators well before they escalate into process instability or plant 
shutdown.</p>
<p className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">This proactive approach often eliminates the need for unnecessary service visits, directly saving 
operational costs. When on-site assistance is required, our service RENVITEK arrives fully 
prepared with targeted solutions, ensuring minimal downtime and uninterrupted plant 
performance.
</p>
        </motion.p>

        {/* ================= SALIENT FEATURES ================= */}
        <motion.h3
          className="text-2xl font-semibold text-emerald-900 dark:text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Salient Features of RENVITEK’s RBHM Technology
        </motion.h3>
        <motion.ul
          className="space-y-3 list-disc pl-6 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {[
            'Scientific Foundation: Developed from peer-reviewed research and decades of real-time biogas plant operational data.',
            'Customization: Each digester has its own unique early warning indicators, tailored to feedstock, climate, and operating conditions.',
            'Validation Phase (3 Months): Site-specific indicators are tested, confirmed, and standardized before full-scale adoption.',
            'Proprietary Titration Method: A simple, affordable, and robust monitoring protocol uniquely developed by RENVITEK; avoids reliance on expensive GC/HPLC instruments.',
            'Proven Reliability: Enables continuous operation for years without stoppage of feeding, preventing costly shutdowns.',
            'Low Manpower Requirement: Does not require highly qualified staff; even a 10th class pass operator can reliably perform monitoring, reducing OPEX.',
            'On-Site Training: RENVITEK provides hands-on training to plant operators, ensuring knowledge transfer, correct application of RBHM methods, and long-term self-sufficiency.',
            'Preventive and Predictive Control: Detects process imbalance weeks in advance, allowing corrective actions before instability occurs.',
            'Cost-Saving: Minimizes service call-outs, downtime, and chemical overuse, directly improving ROI.',
            'Remote Monitoring Enabled: RBHM can be integrated with digital data access for remote diagnostics and expert support.'
          ].map((text, i) => (
            <motion.li key={i} variants={item}>
              {text}
            </motion.li>
          ))}
        </motion.ul>

      </div>
    </section>

   
      {/* Comparison Table */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-4">
              RBHM vs Traditional Methods
            </h2>
            <p className="text-slate-600 dark:text-white/70 tracking-wider">
              Comparison between HPLC/GC VFA profiling and RENVITEK RBHM Technology
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-x-auto"
          >
            <div className="bg-white dark:bg-emerald-900 rounded-lg shadow-lg overflow-hidden min-w-[800px]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-900">
                    <TableHead className="text-white w-1/3">Parameter</TableHead>
                    <TableHead className="text-white w-1/3">GC / HPLC VFA Profiling</TableHead>
                    <TableHead className="text-amber-400 w-1/3">RENVITEK RBHM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow key={index} className={index % 2 === 0 ? 'bg-slate-50 dark:bg-emerald-950/50' : ''}>
                      <TableCell className="font-medium">{row.item}</TableCell>
                      <TableCell className="text-sm whitespace-pre-line text-slate-600 dark:text-white/70">{row.gcHplc}</TableCell>
                      <TableCell className="text-sm whitespace-pre-line text-emerald-600 dark:text-emerald-400 font-medium">{row.rbhm}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </motion.div>
        </div>
      </section>
 <section className="bg-emerald-950 py-12 md:py-10 w-full">
      <div className="w-full px-6 lg:px-16 text-white">
        <motion.h3
          className="text-2xl font-semibold text-amber-400 mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Under the RENVITEK Biodigester Health Monitoring (RBHM) Technology – Annual Maintenance Contract (AMC):
        </motion.h3>

        <motion.p
          className="text-white/80 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
         
          <p>
            RENVITEK provides continuous process surveillance and performance evaluation of Biogas
            CBG plants through advanced monitoring and KPI diagnostics. The RBHM platform enables 
            real-time tracking and trend analysis of key biochemical parameters, including: 
            </p>
            <p>Volatile Fatty Acids (VFAs): To quantify intermediate metabolites and identify early signs of 
            digestion instability.</p>
            <p>Alkalinity Ratio (FOS/TAC): To assess the system’s buffering strength and prevent acidification.Redox Potential: To evaluate the anaerobic environment and microbial health status.
            </p>
            <p>By integrating these indicators within the RBHM framework, clients receive proactive alerts and actionable insights to maintain process stability. Regular monitoring under the AMC ensures consistent methane yield, optimal organic loading, and early detection of operational deviations caused by fluctuations in feedstock composition, OLR, or HRT.</p>
        </motion.p>
      </div>
    </section>
      {/* CTA */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-emerald-950 mb-4">
            RBHM = ₹6 lakh/year Investment
          </h2>
          <p className="text-emerald-900 text-xl mb-8 tracking-wider">
            Protects ₹1.6–5 crore in revenue + ensures contract reliability
          </p>
          <Link to="/contact">
            <motion.button
              className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="rbhm-cta-btn"
            >
              Get RBHM for Your Plant
              <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RBHMPage;
